import { styled } from '@mui/system';
import { Box, Card, Typography, IconButton, LinearProgress, Button } from '@mui/material';
import { colors, fontFamilies } from '../../../constants/theme';
import { SectionHeading, SectionSubtext, DescriptionText } from '../../../ui/typography/styles';

export const Section = styled(Box)(({ theme }) => ({
  backgroundColor: 'transparent',
  width: '100%',
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: '1200px',
  width: '100%',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  [theme.breakpoints.down('md')]: {
    padding: '0 20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
  },
}));

export const TwoColumnLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '32px',
  },
}));

export const CtaWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  height: '60px',
});

export const BenefitsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '41px',
  width: '100%',
  marginTop: '74px',
  marginBottom: '77px',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}));

export const StatisticsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '16px',
  justifyItems: 'center',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(5, 1fr)',
  },
}));

export const StatisticsFlex = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '24px',
  justifyContent: 'center',
  alignItems: 'flex-start',
  width: '100%',
}));

export const HomepageWrapper = styled(Box)({
  position: 'relative',
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
});

export { SectionHeading, SectionSubtext, DescriptionText };

export const CardHeader = styled('h3')(({ theme }) => ({
  fontFamily: fontFamilies?.gotham,
  fontWeight: 500,
  fontSize: '20px',
  lineHeight: '24px',
  letterSpacing: '.2px',
  textAlign: 'start',
  [theme.breakpoints.down('md')]: {
    fontSize: '18px',
    lineHeight: '22px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    lineHeight: '20px',
  },
}));

export const CardTitle = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.gotham || 'Gotham, sans-serif',
  fontWeight: 400,
  color: colors.text,
  fontStyle: 'book',
  fontSize: '20px',
  lineHeight: '100%',
  letterSpacing: '2%',
  [theme.breakpoints.down('md')]: {
    fontSize: '18px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}));

export const CardDescription = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontWeight: 400,
  color: colors.text,
  fontSize: '16px',
  letterSpacing: '0%',
  [theme.breakpoints.down('md')]: {
    fontSize: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
}));

export const ChecklistText = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.quicksand,
  fontSize: '13px',
  fontWeight: 400,
  color: colors.text,
  lineHeight: 1.5,
  textAlign: 'left',
  [theme.breakpoints.up('sm')]: {
    fontSize: '18px',
  },
}));

export const FeatureImage = styled('img')(({ theme }) => ({
  width: '300px',
  height: '200px',
  objectFit: 'none',
  flexShrink: 0,
  background: colors.iconBackground,
  borderRadius: '23px',
  [theme.breakpoints.down('md')]: {
    width: '260px',
    height: '175px',
    borderRadius: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '220px',
    height: '150px',
    borderRadius: '16px',
  },
}));

export const BenefitIcon = styled('img')(({ theme }) => ({
  width: '70px',
  height: '70px',
  objectFit: 'contain',
  flexShrink: 0,
  background: colors.iconBackground,
  padding: '20px',
  borderRadius: '30px',
  [theme.breakpoints.up('md')]: {
    width: '130px',
    height: '130px',
  },
}));

export const StepImage = styled('img')(({ theme }) => ({
  width: '100%',
  objectFit: 'cover',
  display: 'block',
  background: colors.iconBackground,
  borderRadius: '30px',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    height: '250px',
    borderRadius: '20px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    maxWidth: '100%',
    height: '300px',
    borderRadius: '25px',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '450px',
    height: '380px',
    borderRadius: '30px',
  },
}));

export const SectionImage = styled('img')(({ theme }) => ({
  maxWidth: '530px',
  height: '100%',
  display: 'block',
  objectFit: 'cover',
  borderRadius: '30px',
  alignSelf: 'normal',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  [theme.breakpoints.down('md')]: {
    maxWidth: '400px',
    borderRadius: '24px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    borderRadius: '20px',
  },
}));

export const BenefitCardContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '30px',
  [theme.breakpoints.down('md')]: {
    gap: '24px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '16px',
  },
}));

export const BenefitArrowIcon = styled(Box)({
  position: 'absolute',
  top: '-5px',
  right: '-5px',
  color: '#999',
  fontSize: '32px',
});

export const BenefitText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Quicksand',
  fontWeight: 400,
  fontSize: '23px',
  lineHeight: '32px',
  whiteSpace: 'pre-line',

  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}));

export const OutlinedButton = styled(Button)(({ theme }) => ({
  color: '#29405A',
  border: '#29405A 1px solid',
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontSize: '20px',
  fontWeight: 500,
  padding: '23px 30px',
  textTransform: 'none',
  borderRadius: '10px',
  letterSpacing: '.5px',
  '&:hover': {
    borderColor: '#1f3147',
    backgroundColor: 'rgba(41, 64, 90, 0.04)',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '12px 20px',
  },
}));

export const ContainedButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#29405A',
  color: '#FFF',
  fontFamily: fontFamilies?.gotham || 'Gotham, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  padding: '10px 16px',
  textTransform: 'none',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#1f3147',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    padding: '12px 20px',
  },
}));

export const TestimonialText = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontSize: '28px',
  fontWeight: 300,
  color: colors.primary,
  [theme.breakpoints.up('md')]: {
    fontSize: '41px',
  },
}));

export const TestimonialBoldText = styled('span')(({ theme }) => ({
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontWeight: 500,
  color: colors.primary,
}));

export const AuthorName = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.gotham || 'Gotham, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#2c2c2c',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
}));

export const AuthorCredentials = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.gotham || 'Gotham, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#666',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
  },
}));

export const DisclaimerText = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontWeight: 400,
  color: colors.primary,
  textAlign: 'center',
  maxWidth: '950px',
  margin: '0 auto',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
  },
  fontStyle: 'regular',
  fontSize: '22px',
  lineHeight: '28px',
  letterSpacing: '.5px',
}));

export const CollaborativeText = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.gotham || 'Gotham, sans-serif',
  fontSize: '20px',
  fontWeight: 400,
  color: colors.primary,
  lineHeight: '30px',
  textAlign: 'center',
  maxWidth: '950px',
  margin: '0 auto 40px auto',
  [theme.breakpoints.up('sm')]: {
    fontSize: '18px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
  },
  letterSpacing: '2%',
}));

export const HeroSection = styled(Box)(({ theme }) => ({
  color: '#4c2e1f',
  backgroundColor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
  },
}));

export const StyledButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '20px',
  marginTop: '30px',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
  },
}));

export const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  color: colors.primary,
  zIndex: 10,
  '&.Mui-disabled': {
    color: '#999',
  },
}));

export const SectionContainer = styled(Box)(({ theme }) => ({
  padding: '40px 40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '120px',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    padding: '30px 20px',
    marginBottom: '80px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '24px 16px',
    marginBottom: '60px',
  },
}));

export const BenefitCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '48px',
  padding: '20px',
  height: '100%',
  maxWidth: '550px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  boxSizing: 'border-box',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  '&:hover': {},
  [theme.breakpoints.down('md')]: {
    padding: '24px',
    borderRadius: '32px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    borderRadius: '24px',
  },
}));

export const FeatureCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '30px',
  padding: '10px 10px 30px 10px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  maxHeight: '334px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  maxWidth: '320px',
}));

export const StepNumber = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gotham, Quicksand, sans-serif',
  fontSize: '20px',
  fontWeight: 400,
  color: colors.primary,
  letterSpacing: '1px',
  marginBottom: '15px',
  lineHeight: '100%',
}));

export const StepTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gotham, Quicksand, sans-serif',
  fontSize: 32,
  fontWeight: 325,
  color: colors.primary,
  marginBottom: '15px',
}));

export const ChecklistItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '15px',
  alignItems: 'normal',
  marginBottom: '1px',
  '& .MuiSvgIcon-root': {
    marginTop: '2px',
    gap: 0,
  },
  [theme.breakpoints.down('sm')]: {
    gap: '10px',
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
  },
  [theme.breakpoints.up('sm')]: {
    '& .MuiSvgIcon-root': {
      fontSize: '20px',
    },
  },
}));

export const StepCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#fff',
  display: 'flex',
  alignItems: 'flex-start',
  transition: 'transform 0.2s, box-shadow 0.2s',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  [theme.breakpoints.down('sm')]: {
    borderRadius: '24px',
    padding: '8px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    borderRadius: '30px',
    padding: '10px',
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '40px',
    padding: '10px',
  },
}));

export const NumberIcon = styled(Box)(({ theme }) => ({
  fontFamily: fontFamilies?.tenorSans,
  height: '114px',
  width: '100px',
  borderRadius: '30px',
  backgroundColor: colors.iconBackground,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  fontWeight: 400,
  lineHeight: '100%',
  letterSpacing: '5%',
}));

export const StatCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '40px',
  padding: '10px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  maxWidth: '214px',
  height: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  transition: 'transform 0.2s, box-shadow 0.2s',
  [theme.breakpoints.down('md')]: {
    borderRadius: '28px',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '20px',
  },
}));

export const NumberFrame = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '76px',
  borderRadius: '30px 30px 0 0',
  color: colors.primary,
  backgroundColor: colors.iconBackground,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: fontFamilies.tenorSans,
  fontSize: '24px',
  fontWeight: 400,
}));

export const StatPercentage = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies.gotham,
  fontSize: '24px',
  fontWeight: 400,
  marginBottom: '10px',
  lineHeight: 1,
}));

export const StatDescription = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies.quicksand,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: 1.4,
  textAlign: 'left',
}));

export const PaginationDots = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  justifyContent: 'center',
  marginTop: '30px',
}));

export const Dot = styled(Box)(({ active }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: active ? '#2c2c2c' : '#d0d0d0',
  cursor: 'pointer',
  transition: 'background-color 0.3s',
  '&:hover': {
    backgroundColor: active ? '#2c2c2c' : '#999',
  },
}));

export const QuestionCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '16px',
  padding: '0',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  margin: '0 auto',
  overflow: 'hidden',
}));

export const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: '10px',
  borderRadius: '0',
  backgroundColor: '#E8E8E8',
  '& .MuiLinearProgress-bar': {
    backgroundColor: colors.primary,
    borderRadius: '0',
  },
}));

export const OptionButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== '$isModal',
})(({ theme, selected, $isModal }) => ({
  backgroundColor: selected ? colors.lighthBackground : colors.iconBackground,
  borderRadius: '500px',
  padding: '20px 24px',
  marginBottom: '10px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: colors.lighthBackground,
  },
  '&:last-child': {
    marginBottom: 0,
  },
  [theme.breakpoints.up('sm')]: {
    padding: '24px 32px',
    marginBottom: '12px',
  },
  [theme.breakpoints.up('md')]: {
    padding: $isModal ? '16px 20px' : '30px 40px',
  },
}));

export const TestimonialsContentContainer = styled(ContentContainer)({
  alignItems: 'start',
});

export const TestimonialsHeading = styled(SectionHeading)({
  textAlign: 'left',
});

export const TestimonialWrapper = styled(Box)({
  position: 'relative',
  minHeight: '300px',
});

export const TestimonialContent = styled(Box)({
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '24px',
});

export const TestimonialAuthorSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: { xs: 'left', md: 'center' },
  justifyContent: 'space-between',
  flexDirection: { xs: 'column', md: 'row' },
  width: '100%',
}));

export const TestimonialAuthorInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const TestimonialNavigationButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '20px',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

export const NavigationIconButton = styled(IconButton)({
  color: '#2c2c2c',
});
